// Mercapoli - Configuración del Comercio
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, Save, Image, Clock, MapPin, Phone, 
  Mail, FileText, Upload, Loader2, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { comercio as comercioApi } from '../../lib/api';
import { toast } from 'sonner';
import MerchantHeader from '../../components/merchant/MerchantHeader';

const MerchantConfiguracion = () => {
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  const portadaInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [perfil, setPerfil] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    direccion: '',
    telefono: '',
    horario_apertura: '08:00',
    horario_cierre: '22:00',
    logo: '',
    imagen_portada: ''
  });

  const loadPerfil = async () => {
    try {
      const data = await comercioApi.getPerfil();
      setPerfil(data);
      
      if (data.restaurante) {
        setFormData({
          nombre: data.restaurante.nombre || '',
          descripcion: data.restaurante.descripcion || '',
          direccion: data.restaurante.direccion || '',
          telefono: data.restaurante.telefono || '',
          horario_apertura: data.restaurante.horario_apertura || '08:00',
          horario_cierre: data.restaurante.horario_cierre || '22:00',
          logo: data.restaurante.logo || '',
          imagen_portada: data.restaurante.imagen_portada || data.restaurante.imagen || ''
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar perfil del comercio');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPerfil();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre) {
      toast.error('El nombre del comercio es requerido');
      return;
    }

    setSaving(true);
    try {
      await comercioApi.updatePerfil(formData);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar cambios');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantHeader title="Configuración" />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </div>
    );
  }

  if (!perfil?.restaurante) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantHeader title="Configuración" />
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No se encontró un comercio asociado a tu cuenta. Contacta a soporte para resolver este problema.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MerchantHeader title="Configuración del Comercio" />
      
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Información Básica */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                Información del Comercio
              </CardTitle>
              <CardDescription>
                Datos básicos que ven los clientes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre del Comercio *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  placeholder="Ej: Burger Palace"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="descripcion">Descripción</Label>
                <Textarea
                  id="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Describe tu comercio en pocas palabras..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="direccion">Dirección</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="direccion"
                    value={formData.direccion}
                    onChange={(e) => handleChange('direccion', e.target.value)}
                    placeholder="Dirección completa"
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="telefono">Teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="+502 1234 5678"
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Horarios */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Horarios de Atención
              </CardTitle>
              <CardDescription>
                Define cuándo estás disponible para recibir pedidos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="horario_apertura">Hora de Apertura</Label>
                  <Input
                    id="horario_apertura"
                    type="time"
                    value={formData.horario_apertura}
                    onChange={(e) => handleChange('horario_apertura', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="horario_cierre">Hora de Cierre</Label>
                  <Input
                    id="horario_cierre"
                    type="time"
                    value={formData.horario_cierre}
                    onChange={(e) => handleChange('horario_cierre', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Imágenes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                Imágenes
              </CardTitle>
              <CardDescription>
                Logo y portada de tu comercio
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="logo">URL del Logo</Label>
                <Input
                  id="logo"
                  value={formData.logo}
                  onChange={(e) => handleChange('logo', e.target.value)}
                  placeholder="https://ejemplo.com/logo.png"
                />
                {formData.logo && (
                  <div className="mt-2">
                    <img 
                      src={formData.logo} 
                      alt="Logo preview" 
                      className="h-20 w-20 object-cover rounded-lg border"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              <div>
                <Label htmlFor="imagen_portada">URL de Imagen de Portada</Label>
                <Input
                  id="imagen_portada"
                  value={formData.imagen_portada}
                  onChange={(e) => handleChange('imagen_portada', e.target.value)}
                  placeholder="https://ejemplo.com/portada.png"
                />
                {formData.imagen_portada && (
                  <div className="mt-2">
                    <img 
                      src={formData.imagen_portada} 
                      alt="Portada preview" 
                      className="h-32 w-full object-cover rounded-lg border"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Para subir imágenes nuevas, utiliza un servicio de alojamiento de imágenes 
                  (como Cloudinary o Imgur) y pega la URL aquí.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Información de la Cuenta */}
          <Card className="bg-muted/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-5 w-5" />
                Información de la Cuenta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{perfil?.usuario?.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">ID del Comercio</p>
                  <p className="font-medium font-mono text-xs">{perfil?.restaurante?.restaurante_id}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botón Guardar */}
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/merchant')}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-brand-primary hover:bg-brand-primary/90"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MerchantConfiguracion;
